import { counted, language, t, vocabulary } from '../i18n'

export const WEEKDAY_LETTERS = t.calendar.weekdayLetters

export function monthName(date: string): string {
  return t.calendar.months[parseDate(date).getMonth()] ?? ''
}

export function parseDate(date: string): Date {
  const [year, month, day] = date.slice(0, 10).split('-').map(Number)
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1)
}

export function longDate(date: string): string {
  const parsed = parseDate(date)
  return `${t.calendar.weekdays[parsed.getDay()]}, ${parsed.getDate()} ${
    t.calendar.monthsGenitive[parsed.getMonth()]
  }`
}

export function titleDate(date: string): string {
  const long = longDate(date)
  return long.charAt(0).toUpperCase() + long.slice(1)
}

export function shortDate(date: string): string {
  const parsed = parseDate(date)
  return `${t.calendar.weekdaysShort[parsed.getDay()]}, ${parsed.getDate()}`
}

export function dayMonth(date: string): string {
  const parsed = parseDate(date)
  return `${String(parsed.getDate()).padStart(2, '0')} ${
    t.calendar.monthsGenitive[parsed.getMonth()]
  }`
}

const nightEnd = 6 * 60

export function dayMonthYear(date: string): string {
  return `${dayMonth(date)} ${parseDate(date).getFullYear()}`
}

export function today(): string {
  return isoDate(new Date(Date.now() - nightEnd * 60000))
}

export function shiftDate(date: string, offset: number): string {
  const parsed = parseDate(date)
  parsed.setDate(parsed.getDate() + offset)
  return isoDate(parsed)
}

export function isoDate(date: Date): string {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-')
}

export function monthOf(date: string): string {
  return date.slice(0, 7)
}

export function shiftMonth(month: string, offset: number): string {
  const [year, index] = month.split('-').map(Number)
  const date = new Date(year ?? 1970, (index ?? 1) - 1 + offset, 1)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

export function firstOfMonth(month: string): string {
  return `${month}-01`
}

export function lastOfMonth(month: string): string {
  const first = parseDate(`${month}-01`)
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0)
  const end = isoDate(last)
  const now = today()
  return end > now ? now : end
}

export function updates(count: number): string {
  return counted(count, t.units.updates)
}

export function actions(count: number): string {
  return counted(count, t.units.actions)
}

export function products(count: number): string {
  return counted(count, t.units.products)
}

export function days(count: number): string {
  return counted(count, t.units.days)
}

const REMAINING_SHARE: Record<string, number> = {
  full: 100,
  most: 78,
  half: 50,
  low: 18,
  empty: 0,
}

export function remainingShare(level: string): number {
  return REMAINING_SHARE[level] ?? 0
}

export function uses(count: number): string {
  return counted(count, t.units.uses)
}

export function weeks(count: number): string {
  return counted(count, t.units.weeks)
}

export function sinceWeeks(moment: string): number {
  const passed = Date.now() - new Date(moment).getTime()
  return Math.max(0, Math.floor(passed / (7 * 24 * 60 * 60 * 1000)))
}

export function progress(done: number, total: number): string {
  return `${done} ${t.common.of} ${total}`
}

export function percent(rate: number): string {
  return `${Math.round(rate * 100)}%`
}

export const SLOT_TITLES: Record<string, string> = { ...vocabulary.slot }

export function activeColour(category: string): string {
  const known: Record<string, string> = {
    retinoid: 'var(--active-retinoid)',
    acid: 'var(--active-acid)',
    antioxidant: 'var(--active-antioxidant)',
    hydration: 'var(--active-hydration)',
    barrier: 'var(--active-barrier)',
    spf: 'var(--active-spf)',
  }
  return known[category] ?? 'var(--text-muted)'
}

export function frequencyLabel(
  frequency: string,
  daysOfWeek?: string,
  intervalDays?: number,
): string {
  switch (frequency) {
    case 'daily':
      return t.frequency.daily
    case 'days-of-week': {
      const parsed = safeDays(daysOfWeek)
      if (parsed.length === 0) return t.frequency.byWeekday
      return joinWords(parsed.map((day) => t.calendar.weekdaysShort[day] ?? ''))
    }
    case 'every-n-days':
      return intervalDays === 2
        ? t.frequency.everyOtherDay
        : t.frequency.everyNDays(days(intervalDays ?? 0))
    case 'weekly':
      return t.frequency.weekly
    case 'as-needed':
      return t.frequency.asNeeded
    default:
      return frequency
  }
}

function joinWords(words: string[]): string {
  if (words.length < 2) return words.join('')
  return words.slice(0, -1).join(t.frequency.separator) + t.frequency.and + words.at(-1)
}

function safeDays(encoded?: string): number[] {
  if (!encoded) return []
  try {
    const parsed: unknown = JSON.parse(encoded)
    return Array.isArray(parsed)
      ? (parsed.filter((day) => typeof day === 'number') as number[])
      : []
  } catch {
    return []
  }
}

export const TIMEZONES: string[] = (() => {
  const supported =
    typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : []
  const zones = supported.filter((zone) => zone.includes('/') && !zone.startsWith('Etc/'))
  return zones.length > 0 ? zones : Object.keys(t.cities)
})()

const offsets = new Map<string, string>()
const clocks = new Map<string, Intl.DateTimeFormat>()

export function utcOffset(timezone: string): string {
  const cached = offsets.get(timezone)
  if (cached !== undefined) return cached
  const computed = computeOffset(timezone)
  offsets.set(timezone, computed)
  return computed
}

function computeOffset(timezone: string): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      timeZoneName: 'longOffset',
    })
    const part = formatter.formatToParts(new Date()).find((entry) => entry.type === 'timeZoneName')
    const name = part?.value ?? 'GMT'
    const match = /GMT([+-])(\d{2}):(\d{2})/.exec(name)
    if (!match) return 'UTC'
    const [, sign, hours, minutes] = match
    const hour = Number(hours)
    return `UTC${sign}${hour}${minutes === '00' ? '' : `:${minutes}`}`
  } catch {
    return 'UTC'
  }
}

export function utcOffsetMinutes(timezone: string): number {
  const match = /UTC([+-])(\d{1,2})(?::(\d{2}))?/.exec(utcOffset(timezone))
  if (!match) return 0
  const sign = match[1] === '-' ? -1 : 1
  return sign * (Number(match[2]) * 60 + Number(match[3] ?? 0))
}

export function timeIn(timezone: string): string {
  try {
    let clock = clocks.get(timezone)
    if (!clock) {
      clock = new Intl.DateTimeFormat('en-GB', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
      })
      clocks.set(timezone, clock)
    }
    return clock.format(new Date())
  } catch {
    return ''
  }
}

export function cityName(timezone: string): string {
  return t.cities[timezone] ?? timezone.split('/').pop()?.replace(/_/g, ' ') ?? timezone
}

export function timezoneLabel(timezone: string): string {
  return `${utcOffset(timezone)}, ${cityName(timezone)}`
}

export function systemTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export function slotNow(timezone: string, morning: string, evening: string): 'morning' | 'evening' {
  const now = minutesOf(timeIn(timezone))
  if (now < 0) return 'morning'

  if (now < nightEnd) return 'evening'

  const start = minutesOf(morning)
  const end = minutesOf(evening)
  const midpoint = start >= 0 && end > start ? Math.round((start + end) / 2) : 15 * 60
  return now >= midpoint ? 'evening' : 'morning'
}

function minutesOf(value: string): number {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value)
  if (!match) return -1
  return Number(match[1]) * 60 + Number(match[2])
}

export function ordinal(value: number): string {
  if (language === 'ru') return `${value}-го`

  const teen = value % 100
  if (teen >= 11 && teen <= 13) return `${value}th`

  switch (value % 10) {
    case 1:
      return `${value}st`
    case 2:
      return `${value}nd`
    case 3:
      return `${value}rd`
    default:
      return `${value}th`
  }
}

export const photoAccessOpen = (value: string): boolean => {
  if (value === 'always') return true
  if (!value || value === 'off') return false
  const closes = Date.parse(value)
  return Number.isFinite(closes) && closes > Date.now()
}

export const photoAccessChoice = (value: string): string => {
  if (value === 'always' || value === '1h' || value === '24h') return value
  if (!photoAccessOpen(value)) return 'off'
  return Date.parse(value) - Date.now() > 2 * 60 * 60 * 1000 ? '24h' : '1h'
}

export const photoAccessCloses = (value: string): string => {
  const closes = Date.parse(value)
  if (!Number.isFinite(closes) || closes <= Date.now()) return ''
  return new Intl.DateTimeFormat(language === 'ru' ? 'ru-RU' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(closes)
}
